import { useEffect, useMemo, useState } from "react"
import { unwrapResult } from "@reduxjs/toolkit"
import { useAppDispatch, useAppSelector } from "../../../app/hooks"
import { DataGrid, GridColDef } from "@mui/x-data-grid"
import { Localized, useLocalization } from "@fluent/react"
import { Button, ButtonGroup, Paper, TableContainer } from "@mui/material"
import { useNavigate } from "react-router-dom"
import { LoadingContainer } from "../../../app/LoadingContainer"
import { ErrorContainer } from "../../../app/ErrorContainer"
import { selectSettings } from "../../settings/settingsSlice"
import { enUS, plPL } from "@mui/x-data-grid/locales"
import { DocumentStatusLabel } from "../DocumentStatusLabel"
import { QuickSearchToolbar } from "../../../app/QuickSearchToolbar"
import { DocumentHeader, loadImportedDocumentsByFilters } from "../documentsApi"
import { ImportSupplierUrsDialog } from "./ImportSupplierUrsDialog"
import { FlexDiv } from "../../../app/Utils"
import { AddFabButton } from "../../../app/components/AddFabButton"
import NewSmallBadge from "../../../app/components/NewSmallBadge"
import { StructureName } from "../../device/Structure"
import { selectContextStructure } from "../../structure/contextStructureSlice"
import { selectAllStructure, selectStructureAndChildren } from '../../device/structureRelationshipSlice'
import { setRelatedUrs } from "../../ursOffer/importedUrsSlice"

export const ImportedSupplierUrsList = () => {
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const { locale } = useAppSelector(selectSettings);
    const [allDocuments, setAllDocuments] = useState<DocumentHeader[]>([]);
    const { l10n } = useLocalization()
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [isError, setIsError] = useState<boolean>(false)
    const [error, setError] = useState(null);
    const [createDialogVisible, setCreateDialogVisible] = useState(false);
    const { data: structure } = useAppSelector(selectContextStructure)
    const relationship = useAppSelector(state => {
        if (structure && structure.mode === 'manual') {
            return selectStructureAndChildren(state, structure.structId)
        } else {
            return selectAllStructure(state)
        }
    })

    useEffect(() => {
        setIsLoading(true)
        dispatch(loadImportedDocumentsByFilters('URS'))
            .then(unwrapResult)
            .then(setAllDocuments)
            .catch((error) => { console.error(error); setIsError(true); setError(error); })
            .finally(() => setIsLoading(false))
    }, [])

    const documents = useMemo(() => {
        if (structure) {
            return allDocuments.filter(({ structureId }) => relationship.has(structureId));
        } else {
            return allDocuments
        }
    }, [allDocuments, relationship, structure])

    const isToday = (dateString: string) => {
        const date = new Date(dateString);
        const today = new Date();

        return (
            date.getDate() === today.getDate() &&
            date.getMonth() === today.getMonth() &&
            date.getFullYear() === today.getFullYear()
        );
    };

    const handleEditClick = (id) => {
        navigate(`/supplier-imported-urs/edit/${id}`)
    };


    const handleHideDialog = () => {
        setCreateDialogVisible(false);
    }

    const openCreateDialog = () => {
        // setCreateDialogVisible(true);
        dispatch(setRelatedUrs(null))
        navigate(`/supplier-imported-urs/form`)
    };


    const getCustomLocaleText = () => {
        const defaultEnLocaleText = enUS.components.MuiDataGrid.defaultProps.localeText;

        if (locale === 'pl') {
            return plPL.components.MuiDataGrid.defaultProps.localeText;
        } else {
            return defaultEnLocaleText;
        }
    };

    const columns: GridColDef[] =
        [
            {
                field: 'no',
                minWidth: 150,
                headerName: l10n.getString("urs-number", null, "_Numer URS"),
            },
            {
                field: 'version',
                width: 100,
                headerName: l10n.getString("document-version", null, "_Document version"),

            },
            {
                field: 'title',
                headerName: l10n.getString("document-title", null, "_Document title"),
                minWidth: 300,
                flex: 1
            },
            {
                field: 'relatedCompanyName',
                headerName: l10n.getString('document-document-company-owner', null, '_document owner'),
                width: 300
            },
            {
                field: 'structureId',
                headerName: l10n.getString("urs-device-type", null, "_Typ urzadzenia"),
                flex: 1,
                renderCell:(params) =>{
                    return (
                        <StructureName structureId={params.row.structureId} />
                    )
                }
            },
            {
                field: 'createDate',
                width: 150,
                headerName: l10n.getString('spec-created', null, 'Data utworzenia'),
                valueGetter: (_, row) => row.createDate ? new Date(row.createDate) : null,
                renderCell: (params) => {
                    if (!params.value) return "";
                    const formattedDate = params.value.toISOString().split('T')[0];
                    const isNew = isToday(params.row.createDate);

                    return (
                        <>
                            {formattedDate}
                            {isNew && <NewSmallBadge />}
                        </>
                    );
                },
            },
            {
                field: 'status',
                headerName: l10n.getString('meeting-minutes-status', null, 'Status'),
                width: 100,
                renderCell: (params) => {
                    return (
                        <div>
                            <DocumentStatusLabel status={params.row.status} />
                        </div>
                    )
                }
            },
            {
                field: 'actions', 
                type: 'actions', 
                width: 150,
                renderCell: (params) => {
                    return (
                        <ButtonGroup>
                            <Button onClick={() => handleEditClick(params.row.id)} color="secondary" variant="outlined">
                                <Localized id="go-to">
                                    <span>_Przejdz</span>
                                </Localized>
                            </Button>
                        </ButtonGroup>
                    )
                }
            }
        ];


    if (isError) {
        return <>
            <ErrorContainer error={error} />
        </>
    }

    if (isLoading) {
        return <>
            <LoadingContainer />
        </>
    }
    return <>
        <TableContainer component={Paper}>
            <DataGrid
                style={{ minHeight: 100, height: 'calc(100vh - 200px)' }}
                localeText={getCustomLocaleText()}
                rows={documents}
                columns={columns}
                slots={{ toolbar: QuickSearchToolbar }}
                onRowDoubleClick={({ row }) => handleEditClick(row.id)}
                initialState={{ sorting: { sortModel: [{ field: 'createDate', sort: 'desc' }] } }}
            />
            <FlexDiv>
                <AddFabButton onClick={() => { navigate("/supplier-new-import") }} />
            </FlexDiv>
        </TableContainer>
        {
            createDialogVisible &&
            <ImportSupplierUrsDialog onSucces={handleEditClick} onCancel={handleHideDialog} relatedDocument={null} />
        }
    </>
}

